import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useLocation } from "react-router-dom";
import type { RuntimeConfig } from "../runtime-config";
import {
  type AnalyticsClient,
  NOOP_ANALYTICS_CLIENT,
} from "./analytics-client";
import {
  normalizeAnalyticsPath,
  readAnalyticsAttribution,
  type AnalyticsPagePath,
  type AnalyticsRouteKind,
} from "./events";
import { initializePostHogAnalytics } from "./posthog-client";
import { createQueuedAnalyticsClient } from "./queued-analytics-client";

export const ANALYTICS_OPT_OUT_KEY = "analytics_opt_out";
export const ANALYTICS_OPT_OUT_VALUE = "v1";

const OPT_OUT_CHANGED_EVENT = "analytics-opt-out-changed";
let currentTabOptOut: boolean | null = null;

export const ROUTE_KIND_BY_PATH: Readonly<
  Record<AnalyticsPagePath, AnalyticsRouteKind>
> = Object.freeze({
  "/": "landing",
  "/check": "check",
  "/result": "result",
  "/result/shared": "share_confirmation",
  "/r/:id": "shared_result",
  "/privacy": "document",
  "/terms": "document",
  "/about": "document",
});

const AnalyticsContext = createContext<AnalyticsClient>(NOOP_ANALYTICS_CLIENT);

export const readAnalyticsOptOut = (): boolean => {
  if (currentTabOptOut !== null) return currentTabOptOut;
  try {
    return (
      window.localStorage.getItem(ANALYTICS_OPT_OUT_KEY) ===
      ANALYTICS_OPT_OUT_VALUE
    );
  } catch {
    return false;
  }
};

export const setAnalyticsOptOut = (optedOut: boolean): void => {
  currentTabOptOut = optedOut;
  try {
    if (optedOut) {
      window.localStorage.setItem(
        ANALYTICS_OPT_OUT_KEY,
        ANALYTICS_OPT_OUT_VALUE,
      );
    } else {
      window.localStorage.removeItem(ANALYTICS_OPT_OUT_KEY);
    }
  } catch {
    // The current tab still honors the visitor's choice when storage is unavailable.
  }
  window.dispatchEvent(
    new CustomEvent<boolean>(OPT_OUT_CHANGED_EVENT, { detail: optedOut }),
  );
};

const isDoNotTrack = (): boolean => navigator.doNotTrack === "1";
const defaultAnalyticsClientFactory = (): AnalyticsClient =>
  createQueuedAnalyticsClient();

type AnalyticsInitializer = typeof initializePostHogAnalytics;
type AnalyticsGeneration = Readonly<{
  id: number;
  client: AnalyticsClient;
}>;

export function AnalyticsProvider({
  analytics,
  children,
  createAnalyticsClient = defaultAnalyticsClientFactory,
  initializeAnalytics = initializePostHogAnalytics,
  runtimeConfig,
}: Readonly<{
  analytics?: AnalyticsClient;
  children: ReactNode;
  createAnalyticsClient?: () => AnalyticsClient;
  initializeAnalytics?: AnalyticsInitializer;
  runtimeConfig: RuntimeConfig;
}>) {
  const location = useLocation();
  const [generation, setGeneration] = useState<AnalyticsGeneration>(() => ({
    id: 0,
    client: analytics ?? createAnalyticsClient(),
  }));
  const generationIdRef = useRef(0);
  const client = analytics ?? generation.client;
  const [optedOut, setOptedOut] = useState(readAnalyticsOptOut);
  const lastPageKeyRef = useRef<string | null>(null);
  const initializationStartedRef = useRef(false);
  const blocked = runtimeConfig.pilotMode || optedOut || isDoNotTrack();

  useEffect(() => {
    const updateOptOut = (event: Event) => {
      const nextOptOut =
        event instanceof CustomEvent && typeof event.detail === "boolean"
          ? event.detail
          : readAnalyticsOptOut();

      generationIdRef.current += 1;
      client.disable();
      initializationStartedRef.current = false;
      lastPageKeyRef.current = null;

      if (!nextOptOut && analytics === undefined) {
        setGeneration({
          id: generationIdRef.current,
          client: createAnalyticsClient(),
        });
      }
      setOptedOut(nextOptOut);
    };
    window.addEventListener(OPT_OUT_CHANGED_EVENT, updateOptOut);
    return () =>
      window.removeEventListener(OPT_OUT_CHANGED_EVENT, updateOptOut);
  }, [analytics, client, createAnalyticsClient]);

  useEffect(() => {
    if (!blocked) return;
    generationIdRef.current += 1;
    client.disable();
    initializationStartedRef.current = false;
    lastPageKeyRef.current = null;
  }, [blocked, client]);

  useEffect(() => {
    if (blocked) return;

    const pagePath = normalizeAnalyticsPath(location.pathname);
    if (pagePath === null) return;

    const pageKey = pagePath;
    if (lastPageKeyRef.current === pageKey) return;
    lastPageKeyRef.current = pageKey;

    const url = new URL(
      `${location.pathname}${location.search}`,
      window.location.origin,
    );
    client.capture("page_viewed", {
      route_kind: ROUTE_KIND_BY_PATH[pagePath],
      page_path: pagePath,
      ...readAnalyticsAttribution(url, document.referrer),
      schema_version: 1,
    });
  }, [client, location.pathname, location.search, blocked]);

  useEffect(() => {
    if (
      analytics !== undefined ||
      initializationStartedRef.current ||
      blocked ||
      runtimeConfig.posthogProjectToken === null ||
      generation.id !== generationIdRef.current
    )
      return;

    let disposed = false;
    const initialize = () => {
      if (disposed) return;
      initializationStartedRef.current = true;
      const isStaleGeneration = () => generation.id !== generationIdRef.current;
      void initializeAnalytics({
        analytics: client,
        runtimeConfig,
        mode: import.meta.env.MODE,
        isOptedOut: () => isStaleGeneration() || readAnalyticsOptOut(),
        isDoNotTrack: () => isStaleGeneration() || isDoNotTrack(),
      });
    };

    if (typeof window.requestIdleCallback === "function") {
      const idleId = window.requestIdleCallback(initialize);
      return () => {
        disposed = true;
        window.cancelIdleCallback(idleId);
        if (initializationStartedRef.current) {
          generationIdRef.current += 1;
          client.disable();
        }
      };
    }

    const timerId = window.setTimeout(initialize, 0);
    return () => {
      disposed = true;
      window.clearTimeout(timerId);
      if (initializationStartedRef.current) {
        generationIdRef.current += 1;
        client.disable();
      }
    };
  }, [
    analytics,
    blocked,
    client,
    generation.id,
    initializeAnalytics,
    runtimeConfig,
  ]);

  return (
    <AnalyticsContext.Provider value={blocked ? NOOP_ANALYTICS_CLIENT : client}>
      {children}
    </AnalyticsContext.Provider>
  );
}

export const useAnalytics = (): AnalyticsClient => useContext(AnalyticsContext);
